import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GeneralSettings from './GeneralSettings';
import AgentSettings from './AgentSettings';
import UserSettings from './UserSettings';
import SecuritySettings from './SecuritySettings';
import InfrastructureDashboard from './InfrastructureDashboard';
import ScriptRepository from './ScriptRepository';
import BillingSettings from './BillingSettings';

const tabs = [
  { id: 'general', label: 'General', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
  { id: 'billing', label: 'Billing', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
  { id: 'agents', label: 'Agents', icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z' },
  { id: 'users', label: 'Users & Roles', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { id: 'security', label: 'Security', icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
  { id: 'scripts', label: 'Scripts', icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' },
  { id: 'infrastructure', label: 'Infrastructure', icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01' },
];

const tabIds = new Set(tabs.map((t) => t.id));

const SettingsPage: React.FC = () => {
  // ?tab=billing deep-links a tab (the fleet banner and FeatureGate use it).
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(requested && tabIds.has(requested) ? requested : 'general');

  useEffect(() => {
    if (requested && tabIds.has(requested) && requested !== activeTab) setActiveTab(requested);
  }, [requested]); // eslint-disable-line react-hooks/exhaustive-deps

  const select = (id: string) => {
    setActiveTab(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'general') next.delete('tab'); else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-medium text-white">Settings</h2>
        <p className="text-anchor-muted mt-1">Platform configuration and preferences</p>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 p-1 bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => select(tab.id)}
            className={`flex items-center gap-2 py-2.5 px-4 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-anchor-blue text-white shadow-lg shadow-anchor-blue/20'
                : 'text-white/50 hover:text-white/70 hover:bg-white/[0.03]'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
            </svg>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'general' && <GeneralSettings />}
        {activeTab === 'billing' && <BillingSettings />}
        {activeTab === 'agents' && <AgentSettings />}
        {activeTab === 'users' && <UserSettings />}
        {activeTab === 'security' && <SecuritySettings />}
        {activeTab === 'scripts' && <ScriptRepository />}
        {activeTab === 'infrastructure' && <InfrastructureDashboard />}
      </div>
    </div>
  );
};

export default SettingsPage;
